import { useMutation, useQuery, useQueryClient } from "react-query";
import { organizationTypeApi } from "../organizationType";
import type {
    CreateOrganizationTypeRequest,
    UpdateOrganizationTypeRequest,
    OrganizationType,
} from "../../types/organizationType";

// Query keys
export const organizationTypeKeys = {
    all: ["organizationTypes"] as const,
    lists: () => [...organizationTypeKeys.all, "list"] as const,
    list: (filters: string) => [...organizationTypeKeys.lists(), { filters }] as const,
    details: () => [...organizationTypeKeys.all, "detail"] as const,
    detail: (id: number) => [...organizationTypeKeys.details(), id] as const,
};

// Get all organization types
export const useGetOrganizationTypes = () => {
    return useQuery({
        queryKey: organizationTypeKeys.lists(),
        queryFn: organizationTypeApi.getAll,
    });
};

// Get organization type by ID
export const useGetOrganizationType = (id: number) => {
    return useQuery({
        queryKey: organizationTypeKeys.detail(id),
        queryFn: () => organizationTypeApi.getById(id),
        enabled: !!id,
    });
};

// Create organization type
export const useCreateOrganizationType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateOrganizationTypeRequest) => organizationTypeApi.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: organizationTypeKeys.lists() });
        },
    });
};

// Update organization type
export const useUpdateOrganizationType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateOrganizationTypeRequest }) =>
            organizationTypeApi.update(id, data),
        onSuccess: (_data: OrganizationType, { id }: { id: number }) => {
            queryClient.invalidateQueries({ queryKey: organizationTypeKeys.lists() });
            queryClient.invalidateQueries({ queryKey: organizationTypeKeys.detail(id) });
        },
    });
};

// Delete organization type
export const useDeleteOrganizationType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: number) => organizationTypeApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: organizationTypeKeys.lists() });
        },
    });
};
